/* eslint-disable @typescript-eslint/no-unsafe-member-access -- for now */
/* eslint-disable no-magic-numbers -- for now */

/* eslint-disable @typescript-eslint/no-unsafe-assignment -- for now */
/* eslint-disable @typescript-eslint/class-methods-use-this -- for now*/

import { bytesToHex } from "@/utils/hex.utils";
import { type AuthApiResponse, type PasswordApiResponse, type PasswordValidation, PocAuthService, type UserLoginResult, type UserRegistrationResult } from "@/services/abstract/poc/pocAuth.service.interface";

import type { CryptoService } from "../abstract/poc/crypto.service.interface";
import type { FetcherService } from "../abstract/FetcherService";

/**
 * Complete implementation of PocAuthService focused on authentication business logic.
 * Delegates all cryptographic operations to CryptoService via dependency injection.
 */
export class PocAuthServiceImpl extends PocAuthService {

  constructor(private readonly cryptoService: CryptoService, private readonly fetcherService: FetcherService) {
    super();
  }

  // ============= PASSWORD GENERATION =============

  public generateSecurePassword(length = 12): string {
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numberChars = '0123456789';
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    // Ensure at least one of each type
    let password = '';
    password += this.getRandomChar(uppercaseChars);
    password += this.getRandomChar(lowercaseChars);
    password += this.getRandomChar(numberChars);
    password += this.getRandomChar(specialChars);

    // Fill the rest randomly
    const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
    for (let i = 4; i < length; i++) {
      password += this.getRandomChar(allChars);
    }

    return this.shuffleString(password);
  }

  public generateDeterministicPassword(email: string, length = 12): string {
    // Create a seed from the email
    const seed = Array.from(email).reduce((acc, char) => acc + char.charCodeAt(0), 0);

    // Define character sets
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numberChars = '0123456789';
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    // Create a seeded random function
    let currentSeed = seed;
    const seededRandom = (max: number) => {
      // Simple linear congruential generator
      const a = 1664525;
      const c = 1013904223;
      const m = Math.pow(2, 32);

      currentSeed = (a * currentSeed + c) % m;
      return (currentSeed / m) * max;
    };

    // Get a character from a set using seeded random
    const getChar = (charSet: string, index: number) => {
      const position = Math.floor(seededRandom(charSet.length) + index) % charSet.length;
      return charSet.charAt(position);
    };

    // Generate the password with at least one of each character type
    let password = '';
    password += getChar(uppercaseChars, 0);
    password += getChar(lowercaseChars, 1);
    password += getChar(numberChars, 2);
    password += getChar(specialChars, 3);

    // Fill remaining characters
    const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
    for (let i = 4; i < length; i++) {
      password += getChar(allChars, i);
    }

    // Shuffle the password characters deterministically
    const passwordArray = password.split('');
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom(i + 1) + i) % (i + 1);
      [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
    }

    return passwordArray.join('');
  }

  // ============= PASSWORD VALIDATION =============

  public validatePassword(password: string): PasswordValidation {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    };
  }

  public isPasswordValid(validations: PasswordValidation): boolean {
    return Object.values(validations).every(validation => validation === true);
  }

  public isValidPassword(password: string): boolean {
    const validations = this.validatePassword(password);
    return this.isPasswordValid(validations);
  }

  // ============= API OPERATIONS =============

  public async fetchPassword(email: string): Promise<PasswordApiResponse> {
    try {
      const response = await fetch(`https://backend.six3ro.net:8543/api/password/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        hashedPassword: data.hashedPassword,
        message: typeof data.message === 'string' ? data.message : 'Password retrieved successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to fetch password'
      };
    }
  }

  public async savePassword(email: string, password: string): Promise<AuthApiResponse> {
    try {
      // ✅ DELEGATE crypto operations to CryptoService
      const hashedData = await this.cryptoService.hashPassword(password, email);

      const response = await fetch(`https://backend.six3ro.net:8543/setPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          hashedPassword: hashedData.encoded,
          sha512Salt: hashedData.sha512Hash
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data: { message?: string } = await response.json();

      return {
        success: true,
        message: typeof data.message === 'string' ? data.message : 'Password set successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to set password'
      };
    }
  }

  public async sendKeys(email: string, publicKey: Uint8Array, privateKeyHash: string): Promise<AuthApiResponse> {
    try {
      const publicKeyArray = Array.from(publicKey);

      // Save public key for encapsulation
      const encapsResponse = await fetch('https://backend.six3ro.net/encaps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          key: JSON.stringify(publicKeyArray)
        }),
      });

      if (!encapsResponse.ok) {
        throw new Error(`API error (encaps): ${encapsResponse.status}`);
      }

      // Save private key hash for decapsulation
      const deencapsResponse = await fetch('https://backend.six3ro.net/deencaps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          key: privateKeyHash
        }),
      });

      if (!deencapsResponse.ok) {
        throw new Error(`API error (deencaps): ${deencapsResponse.status}`);
      }

      return {
        success: true,
        message: 'Keys stored successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to send keys'
      };
    }
  }

  // ============= HIGH-LEVEL AUTHENTICATION WORKFLOWS =============

  public async registerUser(email: string, password: string): Promise<UserRegistrationResult> {
    try {
      // Validate password
      if (!this.isValidPassword(password)) {
        return {
          success: false,
          message: 'Password does not meet security requirements'
        };
      }

      // ✅ Generate ML-KEM key pair using CryptoService
      const keyPair = await this.cryptoService.generateMlKemKeyPair();

      // ✅ Hash private key for storage using CryptoService
      const privateKeyHash = await this.cryptoService.hashSha256(keyPair.privateKey);
      const privateKeyHashHex = bytesToHex(privateKeyHash);

      // Save password
      const passwordResult = await this.savePassword(email, password);
      if (!passwordResult.success) {
        return {
          success: false,
          message: `Failed to save password: ${passwordResult.message}`
        };
      }

      // Save keys
      const keysResult = await this.sendKeys(email, keyPair.publicKey, privateKeyHashHex);
      if (!keysResult.success) {
        return {
          success: false,
          message: `Failed to save keys: ${keysResult.message}`
        };
      }

      return {
        success: true,
        message: 'User registered successfully',
        keyPair
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }

  public async loginUser(email: string, password: string): Promise<UserLoginResult> {
    try {
      // ✅ Derive key from password using CryptoService
      const derivedKey = await this.cryptoService.deriveKey(password, email);

      // Verify password by fetching and comparing (basic verification)
      const passwordResult = await this.fetchPassword(email);
      if (!passwordResult.success) {
        return {
          success: false,
          message: 'Invalid credentials'
        };
      }

      // Additional verification could be added here
      // For now, if we can derive a key and fetch exists, consider it successful
      return {
        success: true,
        message: 'Login successful',
        derivedKey
      };
    } catch (error) {
      return {
        success: false,
        message: 'Login failed'
      };
    }
  }

  public async verifyPassword(email: string, password: string): Promise<boolean> {
    try {
      // ✅ Hash the provided password using CryptoService
      const providedPasswordHash = await this.cryptoService.hashPassword(password, email);

      // Fetch stored password hash
      const storedPasswordResult = await this.fetchPassword(email);
      if (!storedPasswordResult.success || (storedPasswordResult.hashedPassword == null)) {
        return false;
      }

      // Compare the encoded hashes
      return providedPasswordHash.encoded === storedPasswordResult.hashedPassword;
    } catch (error) {
      return false;
    }
  }

  public async registerUserWithGeneratedPassword(
    email: string
  ): Promise<UserRegistrationResult & { generatedPassword?: string }> {
    try {
      // Generate a secure password
      const generatedPassword = this.generateSecurePassword(16); // Longer for generated passwords

      // Register the user with the generated password
      const registrationResult = await this.registerUser(email, generatedPassword);

      if (registrationResult.success) {
        return {
          ...registrationResult,
          generatedPassword,
          message: `User registered successfully with generated password`
        };
      } else {
        return registrationResult;
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to register user with generated password'
      };
    }
  }

  // ============= PRIVATE HELPER METHODS =============

  private getRandomChar(chars: string): string {
    const randomBytes = this.cryptoService.randomBytes(1);
    return chars.charAt(randomBytes[0] % chars.length);
  }

  private shuffleString(str: string): string {
    const array = str.split('');
    for (let i = array.length - 1; i > 0; i--) {
      const randomBytes = this.cryptoService.randomBytes(2);
      const j = (randomBytes[0] << 8 | randomBytes[1]) % (i + 1);
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array.join('');
  }
}
